import { useMemo } from "react";

import { subSeconds } from "date-fns";

import { useAppSelector } from "@/data/hooks";
import { selectWritingDate } from "@/features/timeline";
import { useAnswerBlock } from "@/graphql/hooks";

/**
 * Buffer to account for upload time, adjust as necessary
 */
export const BUFFER_SECONDS = 30;

/**
 * Get the latest time a new Socratic Dialogue/attempt can start and still have
 * enough time to record before a timed assessment closes.
 */
export function useRecordingStartCutoff(answerBlockId: string): Date | null {
  const writingDate = useAppSelector(selectWritingDate);

  const { data } = useAnswerBlock(answerBlockId);

  return useMemo(() => {
    if (writingDate == null) return null;

    const settings = getSettings(data);
    if (!settings) return null;

    const { readingTime, responseTime } = settings;

    const cutoff = readingTime + responseTime + BUFFER_SECONDS;

    return subSeconds(new Date(writingDate), cutoff);
  }, [writingDate, data]);
}

function getSettings(
  answerBlock: ReturnType<typeof useAnswerBlock>["data"]
): { readingTime: number; responseTime: number } | null {
  const interaction = answerBlock?.question?.body?.fields?.[0]?.interaction;
  if (!interaction) return null;

  if (interaction.__typename !== "SocraticInteraction") return null;

  const { readingTime, responseTime } = interaction;
  if (readingTime == null || responseTime == null) return null;

  return { readingTime, responseTime };
}
